import { useElements, useStripe } from '@stripe/react-stripe-js';
import { useMutation } from '@tanstack/react-query';
import type { UseMutationOptions } from '@tanstack/react-query';
import type { Token } from '@stripe/stripe-js';

export interface CreateStripeTokenInput {
  /** Cardholder name attached to the token. */
  name: string;
}

export const useCreateStripeToken = (
  options?: Omit<UseMutationOptions<Token, Error, CreateStripeTokenInput>, 'mutationFn'>
) => {
  const stripe = useStripe();
  const elements = useElements();

  return useMutation({
    mutationFn: async ({ name }: CreateStripeTokenInput): Promise<Token> => {
      if (!stripe || !elements) {
        throw new Error('Stripe is not ready.');
      }

      const cardElement = elements.getElement('card');
      if (!cardElement) {
        throw new Error('Card details are missing.');
      }

      const { error, token } = await stripe.createToken(cardElement, { name });

      if (error) {
        throw new Error(error.message ?? 'Card could not be processed.');
      }
      if (!token) {
        throw new Error('Card tokenization failed.');
      }

      return token;
    },
    ...options,
  });
};
